import React, { useEffect, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { X as BootstrapX } from 'react-bootstrap-icons';
import { X } from 'lucide-react';

const SlidingSideBar = ({
  containerRef,
  children,
  visible = false,
  title = '',
  showCloseButton = true,
  closeButtonVariant = 'default',
  flush = false,
  panelClassName = '',
  headerClassName = '',
  onClose,
  fullScreen,
  style,
  paddingHorizontal,
  paddingVertical,
  contentStyle,
  width = '100%',
  dock = 'right',
  additionalComponent = null
}) => {
  const Header = () => {
    return (
      <div className={headerClassName}>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1 xlargeFont font-weight-medium">{title}</div>{' '}
          {additionalComponent && <div className="mr-2">{additionalComponent}</div>}
          {showCloseButton &&
            (closeButtonVariant === 'icon' ? (
              <button
                type="button"
                className="sliding-sidebar-close-icon"
                onClick={onClose}
                aria-label="Close"
              >
                <X size={20} strokeWidth={2.75} />
              </button>
            ) : (
              <Button size="sm" className="close_button h-100" onClick={onClose} variant="danger">
                Close <BootstrapX size={20} className="align-text-top" />
              </Button>
            ))}
        </div>
        <hr className="my-2" />
      </div>
    );
  };

  //to prevent scrolling of content underneath, when sidebar is visible
  useEffect(() => {
    if (visible) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Cleanup to ensure we remove the class when component is unmounted or if the sidebar is destroyed/hidden for some other reason
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [visible]);

  const hasPadding = useMemo(
    () => !flush && (paddingHorizontal || paddingVertical),
    [flush, paddingHorizontal, paddingVertical]
  );

  const panelClasses = ['bar-children', hasPadding ? 'rounded' : '', flush ? 'bar-children-flush' : '', panelClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <div style={{ ...style }} className={`bar-container ${visible ? 'bar-visible' : 'bar-collapsed'}`}>
      <div
        className={`bar-content${fullScreen ? '-fullscreen' : ''}${flush ? ' bar-content-flush' : ''}`}
        style={{
          display: 'flex',
          justifyContent: flush ? (dock === 'right' ? 'flex-end' : 'flex-start') : 'center',
          alignItems: flush ? 'stretch' : 'center',
          width,
          height: '100%',
          paddingLeft: flush ? 0 : paddingHorizontal,
          paddingRight: flush ? 0 : paddingHorizontal,
          paddingTop: flush ? 0 : paddingVertical,
          paddingBottom: flush ? 0 : paddingVertical,
          left: dock === 'left' ? (visible ? 0 : '-100%') : 'auto',
          right: dock === 'right' ? (visible ? 0 : '-100%') : 'auto'
        }}
      >
        <div
          style={{ ...contentStyle, backgroundColor: flush ? '#fff' : '#fafafa' }}
          className={panelClasses}
        >
          {(title || showCloseButton) && <Header />}
          <div id="bar-body" ref={containerRef} className="scrollbar-thin">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlidingSideBar;
